/**
 * Навигация по пунктам меню: platform-вкладка, document-вкладка (Alt), dialog.
 */
import type En from '@/common/classes/En'
import { stampCustomWindowTypeOnPageData } from '@/common/utils/adjacentSplitView'
import { useDialog } from '@/common/composables/useDialog'
import { createWindowSlotId } from '@/common/utils/createWindowSlotId'
import { createPlatformActionParams } from '@/common/utils/requestSanitizer'
import { setType } from '@/common/utils/typedValueSerializer'
import { coreService, createSafeClientData } from '@/services/core/core.service'
import type { MenuItem } from '@/services/parse/types'
import type { useAppStore } from '@/stores/app'
import type { useDocumentTabsStore } from '@/stores/documentTabs'
import type { Router } from 'vue-router'

export function isMenuToolbarButton(item: Pick<MenuItem, 'item_type'>): boolean {
  return item.item_type === 'toolbar_button'
}

export function isMenuDialogItem(item: Pick<MenuItem, 'customWindowType'>): boolean {
  return String(item.customWindowType ?? '').trim().toLowerCase() === 'dialog'
}

export function normalizeMenuCustomWindowType(raw: string | undefined): string | undefined {
  const value = String(raw ?? '').trim().toLowerCase()
  return value || undefined
}

function toTypedFields(fields: Record<string, unknown>): Record<string, unknown> {
  const typedFields: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(fields || {})) {
    if (value && typeof value === 'object' && 'type' in value && 'value' in value) {
      typedFields[key] = value
    } else {
      typedFields[key] = setType(value)
    }
  }
  return typedFields
}

export async function resolveMenuItemTypedFields(
  item: MenuItem,
  en: En | null | undefined,
): Promise<Record<string, unknown>> {
  const inUnidataText = item?.in_unidata
  if (!inUnidataText || !en) {
    return {}
  }

  en._resetData()
  en._setFormId(undefined)
  en._setPanelId(undefined)

  try {
    await en.run(inUnidataText)
    return toTypedFields({ ...en._serialize() })
  } catch (error) {
    console.error(`Error executing IN_UNIDATA for menu item ${item?.branch_id}:`, error)
    return {}
  }
}

export type MenuItemNavigationDeps = {
  router: Router
  appStore: ReturnType<typeof useAppStore>
  documentTabsStore: ReturnType<typeof useDocumentTabsStore>
  en?: En | null
}

export async function openMenuItemInDialog(
  item: MenuItem,
  deps: Pick<MenuItemNavigationDeps, 'appStore' | 'en'>,
): Promise<void> {
  const branchId = String(item.branch_id)
  const platform = item.platform || 'WF'
  const typedFields = await resolveMenuItemTypedFields(item, deps.en)

  deps.appStore.setLoading(true, 'Загрузка ...')
  try {
    const dialog = useDialog()
    const result = await dialog.openDialog(branchId, {
      sessionId: null,
      form: typedFields,
      platform,
      parentPageId: deps.appStore.currentPageId,
    })
    if (!result.success) {
      console.error('[menuItemNavigation] Failed to open dialog:', result.error)
    }
  } finally {
    deps.appStore.setLoading(false)
  }
}

export async function openMenuItemInBackground(
  item: MenuItem,
  deps: MenuItemNavigationDeps,
): Promise<void> {
  const branchId = String(item.branch_id)
  const slotId = createWindowSlotId(branchId)
  const platform = item.platform || 'WF'
  const typedFields = await resolveMenuItemTypedFields(item, deps.en)
  const params = createPlatformActionParams({
    sessionId: null,
    isClone: true,
    type: platform,
    actionType: 1,
    actionId: branchId,
    clientData: createSafeClientData({
      fields: typedFields,
    }),
    openingParams: {
      newWindow: true,
      ignoreExistingStack: false,
      tempFid: null,
    },
  })

  deps.appStore.setLoading(true, 'Загрузка ...')
  try {
    const data = (await coreService.action(params)) as Record<string, unknown>
    stampCustomWindowTypeOnPageData(data, item?.custom_window_type ?? item?.customWindowType)
    const { ensureWebClientOnLoadBeforeOpenForm } = await import(
      '@/services/action/webClientFormOnLoadBeforeBuild'
    )
    const prepared = await ensureWebClientOnLoadBeforeOpenForm(data, {
      appStore: deps.appStore,
      openedFormId: branchId,
      parseActionId: branchId,
    })
    deps.appStore.setPageData(slotId, prepared, null)
  } finally {
    deps.appStore.setLoading(false)
  }

  deps.documentTabsStore.openOrFocusDocumentTab(
    slotId,
    deps.appStore.pagesData[slotId] as Record<string, unknown> | undefined,
    'document',
  )

  await deps.router.push({
    name: 'document',
    params: { id: slotId },
  })
}

export async function navigateMenuItem(
  item: MenuItem,
  deps: MenuItemNavigationDeps,
  options?: { openInBackground?: boolean },
): Promise<void> {
  if (options?.openInBackground) {
    await openMenuItemInBackground(item, deps)
    return
  }

  if (isMenuDialogItem(item)) {
    await openMenuItemInDialog(item, deps)
    return
  }

  await deps.router.push({
    name: 'platform',
    params: { id: String(item.branch_id) },
  })
}
